import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';

interface RAGConfiguration {
  id?: number;
  configName: string;
  minHybridTop: number;
  minRerankTop: number;
  kSemantic: number;
  kFinal: number;
  confidenceGeneral: number;
  confidenceImmigration: number;
  geminiModel: string;
  versionNumber?: number;
  isActive?: boolean;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  explanations?: {
    [key: string]: string;
  };
}

interface RAGPromptTemplate {
  id?: number;
  templateName: string;
  templateType: 'domain_classifier' | 'memory_draft' | 'confidence_verifier' | 'grounded_response';
  promptContent: string;
  description?: string;
  versionNumber?: number;
  isActive?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  explanation?: string;
}

interface RAGChangeHistory {
  id?: number;
  changeType: 'config' | 'prompt';
  entityType: string;
  entityName: string;
  oldVersion?: number;
  newVersion?: number;
  changeSummary?: string;
  changedBy?: string;
  changeReason?: string;
  createdAt?: string;
}

const RAGManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'config' | 'prompts' | 'history'>('config');
  const [config, setConfig] = useState<RAGConfiguration | null>(null);
  const [prompts, setPrompts] = useState<RAGPromptTemplate[]>([]);
  const [configHistory, setConfigHistory] = useState<RAGConfiguration[]>([]);
  const [promptHistory, setPromptHistory] = useState<RAGPromptTemplate[]>([]);
  const [changeHistory, setChangeHistory] = useState<RAGChangeHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPromptType, setSelectedPromptType] = useState<string>('domain_classifier');
  const [showConfigHistory, setShowConfigHistory] = useState(false);
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<RAGPromptTemplate | null>(null);

  // Form state
  const [formConfig, setFormConfig] = useState<RAGConfiguration | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [changedBy, setChangedBy] = useState('');

  // OpenRouter models state
  const [openrouterModels, setOpenrouterModels] = useState<Array<{
    id: string;
    name: string;
    description?: string;
    context_length?: number;
  }>>([]);
  const [loadingOpenrouterModels, setLoadingOpenrouterModels] = useState(false);
  const [openrouterModelSearch, setOpenrouterModelSearch] = useState('');

  useEffect(() => {
    loadActiveConfiguration();
    loadActivePrompts();
    loadChangeHistory();
    loadOpenRouterModels();
  }, []);

  const loadOpenRouterModels = async () => {
    setLoadingOpenrouterModels(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/admin/openrouter/models', { headers: authHeaders });
      const data = await response.json();

      if (data.ok && data.models) {
        setOpenrouterModels(data.models);
      } else {
        console.error('Failed to load OpenRouter models:', data.error);
      }
    } catch (error) {
      console.error('Error loading OpenRouter models:', error);
    } finally {
      setLoadingOpenrouterModels(false);
    }
  };

  const loadActiveConfiguration = async () => {
    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/config', { headers: authHeaders });
      const data = await response.json();
      
      if (data.ok) {
        setConfig(data.config);
        setFormConfig({ ...data.config });
      } else {
        console.error('Failed to load configuration:', data.error);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivePrompts = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/prompts', { headers: authHeaders });
      const data = await response.json();
      
      if (data.ok) {
        setPrompts(data.prompts);
      } else {
        console.error('Failed to load prompts:', data.error);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const loadConfigHistory = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/config/history', { headers: authHeaders });
      const data = await response.json();
      
      if (data.ok) {
        setConfigHistory(data.history);
      }
    } catch (error) {
      console.error('Error loading config history:', error);
    }
  };

  const loadPromptHistory = async (templateType?: string) => {
    try {
      const url = templateType
        ? `/api/rag/management/prompts/history?templateType=${templateType}`
        : '/api/rag/management/prompts/history';
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(url, { headers: authHeaders });
      const data = await response.json();
      
      if (data.ok) {
        setPromptHistory(data.history);
      }
    } catch (error) {
      console.error('Error loading prompt history:', error);
    }
  };

  const loadChangeHistory = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/changes', { headers: authHeaders });
      const data = await response.json();
      
      if (data.ok) {
        setChangeHistory(data.changes);
      }
    } catch (error) {
      console.error('Error loading change history:', error);
    }
  };

  const saveConfiguration = async () => {
    if (!formConfig || !changedBy.trim()) {
      alert('Please fill in all required fields including "Changed By"');
      return;
    }

    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/config', {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config: formConfig,
          changeReason: changeReason.trim() || 'Manual update',
          changedBy: changedBy.trim()
        })
      });

      const data = await response.json();
      
      if (data.ok) {
        setConfig(data.config);
        setFormConfig({ ...data.config });
        setChangeReason('');
        alert('Configuration saved successfully!');
        await loadConfigHistory();
        await loadChangeHistory();
      } else {
        alert(`Failed to save configuration: ${data.error}`);
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Error saving configuration');
    } finally {
      setLoading(false);
    }
  };

  const savePromptTemplate = async (prompt: RAGPromptTemplate) => {
    if (!changedBy.trim()) {
      alert('Please enter "Changed By" field');
      return;
    }

    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/prompts', {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          changeReason: changeReason.trim() || 'Manual update',
          changedBy: changedBy.trim()
        })
      });

      const data = await response.json();
      
      if (data.ok) {
        setEditingPrompt(null);
        setChangeReason('');
        alert('Prompt template saved successfully!');
        await loadActivePrompts();
        await loadPromptHistory();
        await loadChangeHistory();
      } else {
        alert(`Failed to save prompt: ${data.error}`);
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Error saving prompt template');
    } finally {
      setLoading(false);
    }
  };

  const restoreConfiguration = async (configId: number) => {
    if (!changedBy.trim()) {
      alert('Please enter "Changed By" field');
      return;
    }

    const reason = prompt('Enter reason for restoration:');
    if (!reason) return;

    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/rag/management/config/restore', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          configId,
          restoredBy: changedBy.trim(),
          reason
        })
      });

      const data = await response.json();
      
      if (data.ok) {
        setConfig(data.config);
        setFormConfig({ ...data.config });
        alert('Configuration restored successfully!');
        await loadConfigHistory();
        await loadChangeHistory();
      } else {
        alert(`Failed to restore configuration: ${data.error}`);
      }
    } catch (error) {
      console.error('Error restoring configuration:', error);
      alert('Error restoring configuration');
    } finally {
      setLoading(false);
    }
  };

  const renderConfigurationTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.section}>
        <h3>🎛️ RAG Configuration Settings</h3>
        <p style={styles.sectionDescription}>
          Configure confidence thresholds and retrieval parameters. Higher values mean stricter requirements and more specialist handoffs.
        </p>

        {formConfig && (
          <div style={styles.formGrid}>
            {/* Confidence Thresholds Section */}
            <div style={styles.configSection}>
              <h4>🎯 Confidence Thresholds</h4>
              <div style={styles.formGroup}>
                <label>
                  <strong>General Questions Confidence</strong>
                  <span style={styles.explanation}>{config?.explanations?.confidenceGeneral}</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formConfig.confidenceGeneral}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    confidenceGeneral: parseFloat(e.target.value) || 0
                  })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label>
                  <strong>Immigration Questions Confidence</strong>
                  <span style={styles.explanation}>{config?.explanations?.confidenceImmigration}</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formConfig.confidenceImmigration}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    confidenceImmigration: parseFloat(e.target.value) || 0
                  })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Retrieval Thresholds Section */}
            <div style={styles.configSection}>
              <h4>🔍 Retrieval Thresholds</h4>
              <div style={styles.formGroup}>
                <label>
                  <strong>Minimum Hybrid Score</strong>
                  <span style={styles.explanation}>{config?.explanations?.minHybridTop}</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formConfig.minHybridTop}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    minHybridTop: parseFloat(e.target.value) || 0
                  })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label>
                  <strong>Minimum Rerank Score</strong>
                  <span style={styles.explanation}>{config?.explanations?.minRerankTop}</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={formConfig.minRerankTop}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    minRerankTop: parseFloat(e.target.value) || 0
                  })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Retrieval Parameters Section */}
            <div style={styles.configSection}>
              <h4>⚙️ Retrieval Parameters</h4>
              <div style={styles.formGroup}>
                <label>
                  <strong>Semantic Search Count (K)</strong>
                  <span style={styles.explanation}>{config?.explanations?.kSemantic}</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formConfig.kSemantic}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    kSemantic: parseInt(e.target.value) || 1
                  })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label>
                  <strong>Final Context Count</strong>
                  <span style={styles.explanation}>{config?.explanations?.kFinal}</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={formConfig.kFinal}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    kFinal: parseInt(e.target.value) || 1
                  })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Model Configuration Section */}
            <div style={styles.configSection}>
              <h4>🤖 AI Model Configuration</h4>
              <div style={styles.formGroup}>
                <label>
                  <strong>AI Model</strong>
                  {loadingOpenrouterModels && <span style={{fontSize: '12px', color: '#718096', marginLeft: '8px'}}> (Loading {openrouterModels.length} models...)</span>}
                  <span style={styles.explanation}>{config?.explanations?.geminiModel || 'LLM model to use for all AI operations (classification, memory responses, confidence verification)'}</span>
                </label>
                <input
                  type="text"
                  placeholder="🔍 Search models... (e.g., gemini, claude, gpt)"
                  value={openrouterModelSearch}
                  onChange={(e) => setOpenrouterModelSearch(e.target.value)}
                  style={{...styles.input, marginBottom: '8px'}}
                />
                <select
                  value={formConfig.geminiModel || 'google/gemini-3-flash-preview'}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    geminiModel: e.target.value
                  })}
                  style={styles.select}
                  disabled={loadingOpenrouterModels}
                >
                  <option value="">Select a model...</option>
                  {openrouterModels
                    .filter(model => {
                      if (!openrouterModelSearch) return true;
                      const search = openrouterModelSearch.toLowerCase();
                      return model.id.toLowerCase().includes(search) ||
                             model.name.toLowerCase().includes(search);
                    })
                    .map(model => (
                      <option key={model.id} value={model.id}>
                        {model.name} ({model.id})
                      </option>
                    ))}
                </select>
                <small style={{display: 'block', marginTop: '4px', color: '#718096'}}>
                  {openrouterModels.length} models available • Selected: {formConfig.geminiModel || 'google/gemini-3-flash-preview'}
                </small>
              </div>

              <div style={styles.formGroup}>
                <label>
                  <strong>Description</strong>
                </label>
                <textarea
                  value={formConfig.description || ''}
                  onChange={(e) => setFormConfig({
                    ...formConfig,
                    description: e.target.value
                  })}
                  style={styles.textarea}
                  placeholder="Optional description of this configuration"
                />
              </div>
            </div>
          </div>
        )}

        {/* Save Section */}
        <div style={styles.saveSection}>
          <div style={styles.formGroup}>
            <label><strong>Changed By (Required)</strong></label>
            <input
              type="text"
              value={changedBy}
              onChange={(e) => setChangedBy(e.target.value)}
              style={styles.input}
              placeholder="Your name/username"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label><strong>Change Reason</strong></label>
            <input
              type="text"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              style={styles.input}
              placeholder="Optional reason for this change"
            />
          </div>

          <div style={styles.buttonGroup}>
            <button 
              onClick={saveConfiguration} 
              disabled={loading}
              style={{...styles.button, ...styles.primaryButton}}
            >
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>
            
            <button 
              onClick={() => setShowConfigHistory(!showConfigHistory)}
              style={{...styles.button, ...styles.secondaryButton}}
            >
              {showConfigHistory ? 'Hide History' : 'Show History'}
            </button>

            <button 
              onClick={loadActiveConfiguration}
              style={{...styles.button, ...styles.secondaryButton}}
            >
              Reset to Current
            </button>
          </div>
        </div>

        {/* Configuration History */}
        {showConfigHistory && (
          <div style={styles.historySection}>
            <h4>📚 Configuration History</h4>
            <div style={styles.historyList}>
              {configHistory.map((historyConfig) => (
                <div key={historyConfig.id} style={styles.historyItem}>
                  <div style={styles.historyHeader}>
                    <span style={styles.versionBadge}>
                      v{historyConfig.versionNumber} 
                      {historyConfig.isActive && <span style={styles.activeBadge}>ACTIVE</span>}
                    </span>
                    <span style={styles.historyDate}>
                      {new Date(historyConfig.createdAt!).toLocaleString()}
                    </span>
                  </div>
                  <div style={styles.historyDetails}>
                    <p><strong>Created by:</strong> {historyConfig.createdBy}</p>
                    <p><strong>Description:</strong> {historyConfig.description || 'No description'}</p>
                    <div style={styles.configValues}>
                      <span>Confidence: G{historyConfig.confidenceGeneral}, I{historyConfig.confidenceImmigration}</span>
                      <span>Thresholds: H{historyConfig.minHybridTop}, R{historyConfig.minRerankTop}</span>
                      <span>Params: K{historyConfig.kSemantic}, F{historyConfig.kFinal}</span>
                    </div>
                  </div>
                  {!historyConfig.isActive && (
                    <button 
                      onClick={() => restoreConfiguration(historyConfig.id!)}
                      style={{...styles.button, ...styles.restoreButton}}
                    >
                      Restore
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPromptsTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.section}>
        <h3>📝 RAG Prompt Templates</h3>
        <p style={styles.sectionDescription}>
          Manage AI prompts used for domain classification, memory responses, confidence verification, and grounded responses.
        </p>

        {/* Prompt Type Selection */}
        <div style={styles.promptTypeSelector}>
          <label><strong>Select Prompt Type:</strong></label>
          <select
            value={selectedPromptType}
            onChange={(e) => setSelectedPromptType(e.target.value)}
            style={styles.select}
          >
            <option value="domain_classifier">Domain Classifier</option>
            <option value="memory_draft">Memory Draft</option>
            <option value="confidence_verifier">Confidence Verifier</option>
            <option value="grounded_response">Grounded Response</option>
          </select>
        </div>

        {/* Active Prompts */}
        <div style={styles.promptsList}>
          {prompts
            .filter(p => p.templateType === selectedPromptType)
            .map((prompt) => (
              <div key={prompt.id} style={styles.promptItem}>
                <div style={styles.promptHeader}>
                  <h4>{prompt.templateName}</h4>
                  <span style={styles.versionBadge}>v{prompt.versionNumber}</span>
                </div>
                
                <div style={styles.promptExplanation}>
                  <strong>Purpose:</strong> {prompt.explanation}
                </div>

                <div style={styles.promptContent}>
                  <pre style={styles.promptText}>{prompt.promptContent}</pre>
                </div>

                <div style={styles.promptActions}>
                  <button 
                    onClick={() => setEditingPrompt(prompt)}
                    style={{...styles.button, ...styles.primaryButton}}
                  >
                    Edit Prompt
                  </button>
                  
                  <button 
                    onClick={() => {
                      setShowPromptHistory(!showPromptHistory);
                      if (!showPromptHistory) {
                        loadPromptHistory(prompt.templateType);
                      }
                    }}
                    style={{...styles.button, ...styles.secondaryButton}}
                  >
                    {showPromptHistory ? 'Hide History' : 'Show History'}
                  </button>
                </div>
              </div>
            ))}
        </div>

        {/* Prompt Editor Modal */}
        {editingPrompt && (
          <div style={styles.modal}>
            <div style={styles.modalContent}>
              <h3>Edit Prompt: {editingPrompt.templateName}</h3>
              
              <div style={styles.formGroup}>
                <label><strong>Template Name</strong></label>
                <input
                  type="text"
                  value={editingPrompt.templateName}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt,
                    templateName: e.target.value
                  })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label><strong>Description</strong></label>
                <input
                  type="text"
                  value={editingPrompt.description || ''}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt,
                    description: e.target.value
                  })}
                  style={styles.input}
                  placeholder="Optional description"
                />
              </div>

              <div style={styles.formGroup}>
                <label><strong>Prompt Content</strong></label>
                <textarea
                  value={editingPrompt.promptContent}
                  onChange={(e) => setEditingPrompt({
                    ...editingPrompt,
                    promptContent: e.target.value
                  })}
                  style={{...styles.textarea, height: '600px'}}
                  placeholder="Enter prompt content..."
                />
              </div>

              <div style={styles.formGroup}>
                <label><strong>Changed By (Required)</strong></label>
                <input
                  type="text"
                  value={changedBy}
                  onChange={(e) => setChangedBy(e.target.value)}
                  style={styles.input}
                  placeholder="Your name/username"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label><strong>Change Reason</strong></label>
                <input
                  type="text"
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  style={styles.input}
                  placeholder="Optional reason for this change"
                />
              </div>

              <div style={styles.buttonGroup}>
                <button 
                  onClick={() => savePromptTemplate(editingPrompt)}
                  disabled={loading}
                  style={{...styles.button, ...styles.primaryButton}}
                >
                  {loading ? 'Saving...' : 'Save Prompt'}
                </button>
                
                <button 
                  onClick={() => setEditingPrompt(null)}
                  style={{...styles.button, ...styles.secondaryButton}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Prompt History */}
        {showPromptHistory && (
          <div style={styles.historySection}>
            <h4>📚 Prompt History for {selectedPromptType}</h4>
            <div style={styles.historyList}>
              {promptHistory
                .filter(p => p.templateType === selectedPromptType)
                .map((historyPrompt) => (
                  <div key={historyPrompt.id} style={styles.historyItem}>
                    <div style={styles.historyHeader}>
                      <span style={styles.versionBadge}>
                        v{historyPrompt.versionNumber}
                        {historyPrompt.isActive && <span style={styles.activeBadge}>ACTIVE</span>}
                      </span>
                      <span style={styles.historyDate}>
                        {new Date(historyPrompt.createdAt!).toLocaleString()}
                      </span>
                    </div>
                    <div style={styles.historyDetails}>
                      <p><strong>Created by:</strong> {historyPrompt.createdBy}</p>
                      <p><strong>Description:</strong> {historyPrompt.description || 'No description'}</p>
                      <details style={styles.promptPreview}>
                        <summary>View Prompt Content</summary>
                        <pre style={styles.promptText}>{historyPrompt.promptContent}</pre>
                      </details>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.section}>
        <h3>📊 Change History & Audit Trail</h3>
        <p style={styles.sectionDescription}>
          Complete audit trail of all configuration and prompt changes with timestamps and reasons.
        </p>

        <div style={styles.historyList}>
          {changeHistory.map((change) => (
            <div key={change.id} style={styles.changeItem}>
              <div style={styles.changeHeader}>
                <span style={styles.changeType}>
                  {change.changeType === 'config' ? '⚙️ Config' : '📝 Prompt'}
                </span>
                <span style={styles.entityName}>{change.entityName}</span>
                <span style={styles.historyDate}>
                  {new Date(change.createdAt!).toLocaleString()}
                </span>
              </div>
              
              <div style={styles.changeDetails}>
                <p><strong>Changed by:</strong> {change.changedBy}</p>
                <p><strong>Entity type:</strong> {change.entityType}</p>
                <p><strong>Version:</strong> {change.oldVersion} → {change.newVersion}</p>
                <p><strong>Summary:</strong> {change.changeSummary}</p>
                {change.changeReason && (
                  <p><strong>Reason:</strong> {change.changeReason}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {changeHistory.length === 0 && (
          <p style={styles.emptyState}>No change history available.</p>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>🎛️ RAG System Management</h2>
        <p>Configure confidence thresholds, prompts, and review change history with full versioning support.</p>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabNav}>
        <button
          onClick={() => setActiveTab('config')}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'config' ? styles.activeTab : {})
          }}
        >
          ⚙️ Configuration
        </button>
        <button
          onClick={() => setActiveTab('prompts')}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'prompts' ? styles.activeTab : {})
          }}
        >
          📝 Prompts
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'history' ? styles.activeTab : {})
          }}
        >
          📊 History
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'config' && renderConfigurationTab()}
      {activeTab === 'prompts' && renderPromptsTab()}
      {activeTab === 'history' && renderHistoryTab()}
    </div>
  );
};

// Styles
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  header: {
    marginBottom: '30px',
    textAlign: 'center' as const
  },
  tabNav: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    borderBottom: '2px solid #e0e0e0'
  },
  tabButton: {
    padding: '12px 24px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s ease'
  },
  activeTab: {
    borderBottomColor: '#007bff',
    backgroundColor: '#f8f9fa',
    color: '#007bff'
  },
  tabContent: {
    minHeight: '600px'
  },
  section: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '24px'
  },
  sectionDescription: {
    color: '#666',
    marginBottom: '24px',
    fontSize: '14px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '30px',
    marginBottom: '30px'
  },
  configSection: {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '20px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '4px'
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '4px'
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginTop: '4px',
    minHeight: '80px',
    resize: 'vertical' as const
  },
  explanation: {
    display: 'block',
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
    fontStyle: 'italic'
  },
  saveSection: {
    borderTop: '2px solid #e0e0e0',
    paddingTop: '24px',
    marginTop: '24px'
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const
  },
  button: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.3s ease'
  },
  primaryButton: {
    backgroundColor: '#007bff',
    color: 'white',
    ':hover': {
      backgroundColor: '#0056b3'
    }
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: 'white'
  },
  restoreButton: {
    backgroundColor: '#28a745',
    color: 'white',
    fontSize: '12px',
    padding: '6px 12px'
  },
  historySection: {
    marginTop: '30px',
    borderTop: '2px solid #e0e0e0',
    paddingTop: '24px'
  },
  historyList: {
    maxHeight: '400px',
    overflowY: 'auto' as const,
    border: '1px solid #e0e0e0',
    borderRadius: '4px'
  },
  historyItem: {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    position: 'relative' as const
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  historyDate: {
    fontSize: '12px',
    color: '#666'
  },
  historyDetails: {
    fontSize: '13px',
    color: '#555'
  },
  versionBadge: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  activeBadge: {
    backgroundColor: '#28a745',
    marginLeft: '8px',
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: '10px'
  },
  configValues: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
    fontSize: '11px',
    color: '#666'
  },
  promptTypeSelector: {
    marginBottom: '24px'
  },
  promptsList: {
    marginBottom: '24px'
  },
  promptItem: {
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '20px',
    marginBottom: '16px'
  },
  promptHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  promptExplanation: {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
    fontSize: '13px',
    color: '#555'
  },
  promptContent: {
    marginBottom: '16px'
  },
  promptText: {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'pre-wrap' as const,
    maxHeight: '200px',
    overflow: 'auto',
    border: '1px solid #ddd'
  },
  promptActions: {
    display: 'flex',
    gap: '12px'
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '8px',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    width: '90%'
  },
  promptPreview: {
    marginTop: '8px'
  },
  changeItem: {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0'
  },
  changeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  changeType: {
    fontSize: '14px',
    fontWeight: 'bold'
  },
  entityName: {
    backgroundColor: '#e9ecef',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px'
  },
  changeDetails: {
    fontSize: '13px',
    color: '#555'
  },
  emptyState: {
    textAlign: 'center' as const,
    color: '#666',
    fontStyle: 'italic',
    padding: '40px'
  }
};

export default RAGManagement;